import React from "react"
import cx from "classnames"
import { Tabs as TabsPrimitive } from "@base-ui/react/tabs"
import { CountBadge } from "../count-badge"
import Icon from "../icon"
import type { IconName } from "../icon/assets/icon-map"

// =============================================================================
// Props
// =============================================================================

export type PageSecondaryHeaderTabProps = {
  children?: React.ReactNode
  className?: string
  /**
   * Optional count rendered in a `CountBadge` after `children`.
   */
  count?: number
  /**
   * Optional icon rendered before `children`.
   */
  icon?: IconName
  /**
   * The value identifying this tab. Must match the `value` of the corresponding
   * `PageSecondaryHeader.TabPanel`. The tab is active when `Tabs.Root`'s `value`
   * (or `defaultValue`) equals this value.
   */
  value: TabsPrimitive.Tab.Value
}

// =============================================================================
// Styles
// =============================================================================

const pageSecondaryHeaderTabStyles = [
  "outline-2 outline-transparent",
  "border-b border-transparent data-[active]:border-gray-900",
  "py-5",
  "inline-flex items-center gap-x-2",
  "text-sm leading-6 font-semibold text-gray-700 data-[active]:text-gray-900",
  "transition-colors duration-200",
  "hover:border-gray-700 data-[active]:hover:border-gray-900 focus-visible:outline-gray-900"
]

// =============================================================================
// Component
// =============================================================================

/**
 * Secondary page header tab.
 *
 * Renders Base UI's `Tabs.Tab` as a `<button role="tab">`. Active state is
 * derived from the ancestor `PageSecondaryHeader.TabsRoot`'s `value` matching
 * this tab's `value`.
 *
 * Must be a descendant of `PageSecondaryHeader.Tabs`.
 */
export function PageSecondaryHeaderTab({
  children,
  className,
  count,
  icon,
  value
}: PageSecondaryHeaderTabProps) {
  return (
    <TabsPrimitive.Tab
      value={value}
      className={cx(pageSecondaryHeaderTabStyles, className)}
    >
      {icon && <Icon name={icon} size="xs" aria-hidden="true" />}
      {children}
      {count !== undefined && <CountBadge count={count} />}
    </TabsPrimitive.Tab>
  )
}
